import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories, mainSiteUrl } from '@/mocks/categories';
import Footer from '@/pages/home/components/Footer';
import Navbar from '@/pages/home/components/Navbar';

function generateSchema(article: typeof articles[0]) {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    image: article.imageUrl,
    author: { '@type': 'Organization', name: 'Oopbuy Spreadsheet Guide' },
    datePublished: article.datePublished,
    dateModified: article.datePublished,
    inLanguage: 'de',
    publisher: { '@type': 'Organization', name: 'Oopbuy Spreadsheet Guide', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg' } },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Startseite',
        item: 'https://oopbuyspread.store/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: `https://oopbuyspread.store/artikel/${article.slug}`,
      },
    ],
  };

  const schemas = [articleSchema, breadcrumbSchema];

  if (article.faqs && article.faqs.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    });
  }

  return schemas;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const schemas = generateSchema(article);

  return (
    <>
      {schemas.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }}
        />
      ))}

      <Navbar />

      <nav className="w-full bg-background-50 border-b border-background-200/70 mt-16" aria-label="Breadcrumb">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">
                Startseite
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
            </li>
            <li className="text-foreground-800 font-medium truncate max-w-[240px] md:max-w-md">
              {article.title}
            </li>
          </ol>
        </div>
      </nav>

      <main className="bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-3xl">
              {article.metaDescription}
            </p>
          </header>

          {article.imageUrl && (
            <div className="mb-12 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.imageAlt}
                title={article.title}
                className="w-full h-auto object-cover"
              />
            </div>
          )}

          <div className="space-y-12">
            {article.sections.map((section, sIdx) => (
              <section key={sIdx}>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
                  {section.heading}
                </h2>

                {section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className="text-foreground-700 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}

                {section.listItems && section.listItems.length > 0 && (
                  section.isOrdered ? (
                    <ol className="space-y-3 text-foreground-700 list-decimal list-inside mb-4">
                      {section.listItems.map((item, lIdx) => (
                        <li key={lIdx} className="leading-relaxed pl-2">{item}</li>
                      ))}
                    </ol>
                  ) : (
                    <ul className="space-y-3 mb-4 text-foreground-700">
                      {section.listItems.map((item, lIdx) => (
                        <li key={lIdx} className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center text-accent-600 mt-0.5 flex-shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )
                )}

                {section.tableData && section.tableData.length > 0 && (
                  <div className="overflow-x-auto mt-4">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-background-100">
                          <th className="text-left py-3 px-4 font-semibold text-foreground-900 border-b border-background-200/70">
                            Kriterium
                          </th>
                          <th className="text-center py-3 px-4 font-semibold text-primary-600 border-b border-background-200/70">
                            Oopbuy Spreadsheet
                          </th>
                          <th className="text-center py-3 px-4 font-semibold text-foreground-600 border-b border-background-200/70">
                            Alternative / Traditionell
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.tableData.map((row, rIdx) => (
                          <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                            <td className="py-3 px-4 text-foreground-800 font-medium">{row.criteria}</td>
                            <td className="text-center py-3 px-4 text-accent-700 font-medium">
                              <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-600 mx-auto mb-1"></i>
                              {row.oopbuy}
                            </td>
                            <td className="text-center py-3 px-4 text-foreground-500">{row.traditional}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}

            {article.faqs && article.faqs.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5">
                  Häufig gestellte Fragen
                </h2>
                <div className="space-y-4">
                  {article.faqs.map((faq, idx) => (
                    <div key={idx} className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                      <h3 className="font-semibold text-foreground-900 mb-2">{faq.q}</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* CTA */}
            <section className="bg-primary-50/50 border border-primary-200/50 rounded-lg p-8 md:p-10 text-center">
              <h2 className="font-heading text-2xl font-semibold text-foreground-900 mb-4">
                Bereit, das Oopbuy Spreadsheet zu nutzen?
              </h2>
              <p className="text-foreground-600 mb-6 max-w-lg mx-auto">
                Entdecke jetzt die besten Produkte und Verkäufer. Besuche unsere Hauptseite und starte noch heute.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300"
                >
                  <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
                  Oopbuy Spreadsheet
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-secondary-500 text-white font-semibold text-sm hover:bg-secondary-600 transition-all duration-300"
                >
                  <i className="ri-flashlight-line w-4 h-4 flex items-center justify-center"></i>
                  Buy It Now
                </a>
                <Link
                  to="/"
                  className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-background-200 text-foreground-800 font-semibold text-sm hover:bg-background-300 transition-all duration-300"
                >
                  <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                  Zurück zur Startseite
                </Link>
              </div>
            </section>

            {/* Related Categories */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                Alle Oopbuy Spreadsheet Kategorien
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-between bg-background-100 border border-background-200/70 rounded-lg px-4 py-3 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                  >
                    <span className="font-medium text-sm text-foreground-800">{cat.name}</span>
                    <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                  </a>
                ))}
              </div>
            </section>

            {/* Related Articles */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                Weitere Oopbuy Spreadsheet Guides
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 6)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/artikel/${a.slug}`}
                      className="bg-background-100 border border-background-200/70 rounded-lg px-4 py-3 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <span className="text-xs text-foreground-500 mb-1 block">{a.category}</span>
                      <span className="font-medium text-sm text-foreground-800 line-clamp-2">{a.title}</span>
                    </Link>
                  ))}
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}